/**
 * Status utility functions for consistent status handling across the application
 */

/**
 * Get Bootstrap variant for status badges
 * @param {string} status - The status value
 * @returns {string} Bootstrap variant class
 */
export const getStatusVariant = (status) => {
  switch (status) {
    case 'Checked and Verified by Extension Head':
      return 'success';
    case 'Approved':
    case 'Approved by Extension Head':
    case 'Approved by VC-RDES':
    case 'Approved by VC-AF':
    case 'Approved by Chancellor':
    case 'Active':
    case 'Completed':
      return 'success';
    case 'Rejected':
    case 'Rejected by Extension Head':
    case 'Rejected by VC-RDES':
    case 'Rejected by VC-AF':
    case 'Rejected by Chancellor':
    case 'Rejected by Admin':
    case 'Overdue':
      return 'danger';
    case 'On-Going':
    case 'Pending':
    case 'Reviewed by GAD':
      return 'warning';
    default:
      return 'secondary';
  }
};

/**
 * Get Bootstrap variant for monitoring type badges
 * @param {string} type - The monitoring type
 * @returns {string} Bootstrap variant class
 */
export const getTypeVariant = (type) => {
  switch (type) {
    case 'Progress':
      return 'primary';
    case 'Financial':
      return 'success';
    case 'Impact':
      return 'info';
    case 'Quality':
      return 'warning';
    default:
      return 'secondary';
  }
};

/**
 * Get icon class for status
 * @param {string} status - The status value
 * @returns {string} Bootstrap icon class
 */
export const getStatusIcon = (status) => {
  switch (status) {
    case 'Approved':
    case 'Approved by Extension Head':
    case 'Approved by VC-RDES':
    case 'Approved by VC-AF':
    case 'Approved by Chancellor':
    case 'Active':
      return 'bi-check-circle-fill';
    case 'Rejected':
    case 'Rejected by Extension Head':
    case 'Rejected by VC-RDES':
    case 'Rejected by VC-AF':
    case 'Rejected by Chancellor':
    case 'Rejected by Admin':
      return 'bi-x-circle-fill';
    case 'On-Going':
    case 'Pending':
    case 'Reviewed by GAD':
      return 'bi-clock-fill';
    case 'Completed':
      return 'bi-check-square-fill';
    case 'Overdue':
      return 'bi-exclamation-triangle-fill';
    default:
      return 'bi-question-circle-fill';
  }
};

/**
 * Get display text for status
 * @param {string} status - The status value
 * @returns {string} Human-readable status text
 */
export const getStatusDisplayText = (status) => {
  const statusMap = {
    'On-Going': 'On-Going',
    'Pending': 'Pending',
    'Approved': 'Approved',
    'Rejected': 'Rejected',
    'Reviewed by GAD': 'Reviewed by GAD',
    'Checked and Verified by Extension Head': 'Checked and Verified by Extension Head',
    'Approved by Extension Head': 'Approved by Extension Head',
    'Rejected by Extension Head': 'Rejected by Extension Head',
    'Approved by VC-RDES': 'Approved by VC for RDES',
    'Rejected by VC-RDES': 'Rejected by VC for RDES',
    'Approved by VC-AF': 'Approved by Vice Chancellor for Administration and Finance',
    'Rejected by VC-AF': 'Rejected by Vice Chancellor for Administration and Finance',
    'Approved by Chancellor': 'Approved by Chancellor',
    'Rejected by Chancellor': 'Rejected by Chancellor',
    'Rejected by Admin': 'Rejected by Admin',
    'Completed': 'Completed',
    'Active': 'Active',
    'Overdue': 'Overdue'
  };
  
  return statusMap[status] || status;
};

/**
 * Check if status indicates completion
 * @param {string} status - The status value
 * @returns {boolean} True if status indicates completion
 */
export const isCompletedStatus = (status) => {
  return ['Completed'].includes(status);
};

/**
 * Check if status indicates active/in-progress
 * @param {string} status - The status value
 * @returns {boolean} True if status indicates active
 */
export const isActiveStatus = (status) => {
  return ['Active', 'On-Going', 'Pending', 'Reviewed by GAD', 'Checked and Verified by Extension Head', 'Approved by Extension Head', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status);
};
